import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useErrorReporter } from "@/components/error-reporter";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { sendOtp } from "@/lib/auth/otp-functions";
import { verifyOtp } from "@/lib/auth/otp-verify-functions";
import { createAccount } from "@/lib/auth/signup-functions";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Sign in — Subiza" }] }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("auth");
  const [otpCode, setOtpCode] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { reportError } = useErrorReporter();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: "/dashboard", replace: true });
    });
    // Intentionally runs once on mount. `navigate` is stable for the lifetime
    // of the router, and depending on it here would re-run this check (and
    // potentially redirect) on every router state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) return reportError(error, { area: "auth.signIn" });
    await router.invalidate();
    navigate({ to: "/dashboard", replace: true });
  }

  async function handleSignup(e) {
    e.preventDefault();
    setLoading(true);

    // Create the account through the service-role admin API, never through
    // `supabase.auth.signUp`. The public endpoint makes GoTrue send its own
    // confirmation email - delivered from Supabase's address, not the Gmail
    // sender configured in `.env` - and it is subject to the public sign-up
    // limiter. The admin endpoint sends no email, so the branded code sent by
    // `sendOtp` below is the only verification message the user receives.
    try {
      await createAccount({ data: { email, password, name } });
    } catch (signupError) {
      setLoading(false);
      return reportError(signupError, { area: "auth.signUp" });
    }

    try {
      await sendOtp({ data: { email, name } });
      setStep("verify");
      setLoading(false);
      toast.success("Verification code sent to your email.");
    } catch (err) {
      setLoading(false);
      reportError(err, { area: "auth.signup.sendOtp" });
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setLoading(true);

    try {
      await verifyOtp({ data: { email, code: otpCode } });

      // The server has marked the address verified and confirmed the auth
      // user, so a password sign-in is now guaranteed to succeed and to return
      // a session. Establishing it here is what makes the navigation below
      // stick: navigating straight to /dashboard without it bounced the user
      // back to /auth, because the guard requires a live session.
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

      if (signInError) {
        setLoading(false);
        return reportError(signInError, { area: "auth.verifyOtp.signIn" });
      }

      setOtpCode("");
      setLoading(false);
      toast.success("Email verified successfully!");

      await router.invalidate();
      navigate({ to: "/dashboard", replace: true });
    } catch (err) {
      setLoading(false);
      reportError(err, { area: "auth.verifyOtp" });
    }
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    setResendLoading(true);
    try {
      await sendOtp({ data: { email, name } });
      setResendCooldown(60);
      toast.success("Code resent — check your inbox or spam folder.");
    } catch (err) {
      reportError(err, { area: "auth.resendOtp" });
    } finally {
      setResendLoading(false);
    }
  }

  async function handleGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + "/dashboard" },
    });
    if (error) return toast.error("Google sign-in failed.");
  }

  return (
    <div className="min-h-screen bg-hero">
      <Header />
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <Card className="glass w-full max-w-md p-6 shadow-elegant border-2 sm:p-8">
          {step === "auth" && (
            <>
              <h1 className="text-3xl font-bold tracking-tight">Teacher & Admin</h1>
              <p className="text-sm text-muted mt-1">Sign in to create and host quizzes.</p>

              <Tabs defaultValue="login" className="mt-6">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="login">Sign in</TabsTrigger>
                  <TabsTrigger value="signup">Sign up</TabsTrigger>
                </TabsList>
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-3 mt-4">
                    <div>
                      <Label htmlFor="e1">Email</Label>
                      <Input
                        id="e1"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="p1">Password</Label>
                      <Input
                        id="p1"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-gradient-primary">
                      Sign in
                    </Button>
                  </form>
                </TabsContent>
                <TabsContent value="signup">
                  <form onSubmit={handleSignup} className="space-y-3 mt-4">
                    <div>
                      <Label htmlFor="n2">Full name</Label>
                      <Input
                        id="n2"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="e2">Email</Label>
                      <Input
                        id="e2"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="p2">Password</Label>
                      <Input
                        id="p2"
                        type="password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-gradient-primary">
                      Create account
                    </Button>
                    <p className="text-center text-xs text-muted">
                      By creating an account you agree to our{" "}
                      <Link
                        to="/terms"
                        className="text-accent underline underline-offset-2 hover:no-underline"
                      >
                        Terms and Conditions
                      </Link>
                      .
                    </p>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="flex items-center gap-3 my-5 text-xs text-muted">
                <div className="h-px flex-1 bg-border" /> OR{" "}
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button variant="outline" className="w-full" onClick={handleGoogle}>
                Continue with Google
              </Button>
            </>
          )}

          {step === "verify" && (
            <>
              <h1 className="text-3xl font-bold tracking-tight">Verify your email</h1>
              <p className="text-sm text-muted mt-2">
                We sent a 6-digit code to <span className="font-medium">{email}</span>
              </p>
              <p className="text-xs text-muted mt-1">
                Didn't see it? Check your spam or promotions folder, then use the resend button
                below.
              </p>
              <form onSubmit={handleVerify} className="space-y-6 mt-6">
                <div className="flex flex-col items-center space-y-4">
                  <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="size-8 sm:size-9" />
                      <InputOTPSlot index={1} className="size-8 sm:size-9" />
                      <InputOTPSlot index={2} className="size-8 sm:size-9" />
                      <InputOTPSlot index={3} className="size-8 sm:size-9" />
                      <InputOTPSlot index={4} className="size-8 sm:size-9" />
                      <InputOTPSlot index={5} className="size-8 sm:size-9" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  type="submit"
                  disabled={loading || otpCode.length < 6}
                  className="w-full bg-gradient-primary"
                >
                  Verify code
                </Button>
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResend}
                    disabled={resendLoading || resendCooldown > 0}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Clear the partial code so returning to this step starts
                      // from a blank field rather than a half-entered one.
                      setOtpCode("");
                      setResendCooldown(0);
                      setStep("auth");
                    }}
                  >
                    Back to sign up
                  </Button>
                </div>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
